'use client';
import IconCircleInfo from '@mnd-frontend/icons/components/CircleInfo';
import IconSparkle from '@mnd-frontend/icons/components/Sparkle';
import IconTriangleWarning from '@mnd-frontend/icons/components/TriangleWarning';
import type { Prettify } from '@mnd-frontend/ts';
import { SPACING_1, SPACING_2 } from '@mnd-frontend/ui/shared/tokens';
import type { MouseEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { Button } from '../Button';
import { Spinner } from '../Spinner';
import { Text } from '../Text';
import { breakpoints } from '../utils/media';

type AlertProps = Prettify<CommonProps & (WithoutIconProps | IconProps | SmartAlertProps)>;

type ActionButton = {
  text: string;
  onClick: (e: MouseEvent) => void;
  href?: undefined;
  loading?: boolean;
};
type ActionAnchor = {
  text: string;
  href: string;
  onClick?: (event: MouseEvent) => void;
  loading?: boolean;
};

type ActionProp = Prettify<ActionButton | ActionAnchor>;

type CommonProps = {
  children: ReactNode;
  action?: ActionProp;
  title?: string;
  loading?: boolean;
};

interface WithoutIconProps {
  type: 'info' | 'warning' | 'error';
  action?: ActionProp;
}

interface IconProps {
  type?: 'default';
  icon?: ReactNode;
}

type SmartAlertProps = Prettify<{ type: 'smart' } & (SmartAlertBar | SmartAlertBox)>;

type SmartAlertBar = {
  variant?: 'bar';
  action?: ActionProp;
};

type SmartAlertBox = {
  variant: 'box';
  action: ActionProp;
};

type BreakpointPixcel = `${(typeof breakpoints)[keyof typeof breakpoints]}px`;
const breakpointTablet: BreakpointPixcel = `${breakpoints.tablet}px`;

const IconWrapper = styled.div`
  line-height: 0;
  > * {
    flex-grow: 1;
  }
`;

const WRAPPER_BASE_STYLE = styled.aside`
  text-align: left;
  color: ${props => props.theme.colorTextBodyPrimary};
  border-radius: ${props => props.theme.radiusAlert};

  > p {
    font-size: 0.875rem;

    @media (min-width: ${breakpointTablet}) {
      font-size: 1rem;
    }
  }
`;

const Wrapper = styled(WRAPPER_BASE_STYLE)<{
  $type: AlertProps['type'];
  $hasButton: boolean;
}>`
  display: grid;
  column-gap: ${SPACING_2};
  align-items: center;
  min-height: ${props => props.theme.sizingAlertDefault};
  padding: ${SPACING_2};

  ${props =>
    props.$hasButton
      ? css`
          grid-template-rows: auto 3rem;
          grid-template-columns: 1.5rem 1fr;

          > button,
          > a {
            grid-column: 1 / 3;
          }

          @media (min-width: ${breakpointTablet}) {
            grid-template-rows: 1fr;
            grid-template-columns: 1.5rem 1fr auto;

            > button,
            > a {
              grid-row: 1;
              grid-column: 3 / 4;
            }
          }
        `
      : css`
          grid-template-columns: auto 1fr auto;
        `}

  ${props => {
    switch (props.$type) {
      case 'error':
        return css`
          background: ${props.theme.colorBackgroundAlertError};
        `;
      case 'info':
        return css`
          background: ${props.theme.colorBackgroundAlertInfo};
        `;
      case 'smart':
        return css`
          background: ${props.theme.colorBackgroundAlertSmart};
        `;
      case 'warning':
        return css`
          background: ${props.theme.colorBackgroundAlertWarning};
        `;
      default:
        return css`
          background: ${props.theme.colorBackgroundAlertDefault};
        `;
    }
  }}
`;

const WrapperBox = styled(WRAPPER_BASE_STYLE)`
  display: flex;
  flex-direction: column;
  gap: ${SPACING_1};
  background: ${props => props.theme.colorBackgroundAlertSmart};
  padding: ${SPACING_2};
  max-width: 30rem;

  .footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${SPACING_2};

    button {
      text-wrap: wrap;
      text-align: left;
      max-width: 85%;
    }

    svg {
      color: ${props => props.theme.colorIconsSmart};
    }
  }
`;

const AlertText = ({ title, children }: { children: ReactNode; title?: string }) => (
  <Text as="p" variant="body">
    {title && (
      <Text as="span" variant="labelLarge">
        {title}{' '}
      </Text>
    )}
    {children}
  </Text>
);

const isAnchorAction = (action: ActionProp): action is ActionAnchor => {
  return typeof action.href === 'string';
};

const AlertButton = ({ action }: { action: ActionProp }) => {
  if (!action) return null;
  return (
    <Button
      configuration="text"
      {...(isAnchorAction(action) ? { ...action } : { ...action, type: 'button' })}
    >
      {action.text}
    </Button>
  );
};

export const Alert = ({ type, action, children, title, loading, ...props }: AlertProps) => {
  const { t } = useTranslation();

  if ('variant' in props && props.variant === 'box') {
    return (
      <WrapperBox>
        <AlertText title={title}>{children}</AlertText>
        <div className="footer">
          <AlertButton action={action as NonNullable<ActionProp>} />
          <IconSparkle aria-label={t('common_ai_suggestion')} />
        </div>
      </WrapperBox>
    );
  }

  let icon = null;
  if ('icon' in props) {
    icon = props.icon ?? null;
  } else if (type === 'error') {
    icon = <IconTriangleWarning $color="DANGER_700" $size="xs" />;
  } else if (type === 'info') {
    icon = <IconCircleInfo $color="colorIconsInfo" $size="xs" />;
  } else if (type === 'warning') {
    icon = <IconTriangleWarning $color="WARNING_700" $size="xs" aria-label={t('common_warning')} />;
  } else if (type === 'smart') {
    icon = (
      <IconSparkle $color="colorIconsDefault" $size="xs" aria-label={t('common_ai_suggestion')} />
    );
  }

  return (
    <Wrapper $type={type} $hasButton={!!action}>
      {icon && <IconWrapper>{icon}</IconWrapper>}
      <AlertText title={title}>{children}</AlertText>
      {action && <AlertButton action={action} />}
      {loading && <Spinner size="sm" />}
    </Wrapper>
  );
};
